import { Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { MarketingNav } from "@/components/Marketing/MarketingNav";
import { MarketingFooter } from "@/components/Marketing/MarketingFooter";
import { MarketingFinalCta } from "@/components/Marketing/MarketingFinalCta";

const SECTIONS = [
  {
    title: "Platform & institutions",
    items: [
      { q: "Is CONORBIT shared across institutions?", a: "Every institution gets its own fully isolated account with separate data, branding, roles, and audit logs. Row-level security is enforced throughout the platform, not just at the application layer." },
      { q: "Can one person belong to more than one institution?", a: "Yes, for staff with legitimate access to multiple organisations, membership is scoped per institution, so permissions in one institution never leak into another." },
      { q: "Which languages are supported?", a: "English, French and Portuguese ship today across the institution dashboard and journalist portal. Additional languages are available on request for Enterprise plans." },
      { q: "Can we self-host or run on our own infrastructure?", a: "Enterprise customers can discuss on-prem or dedicated infrastructure deployment. Otherwise, CONORBIT runs on hardened cloud infrastructure managed by our team." },
    ],
  },
  {
    title: "Social Suite",
    items: [
      { q: "Which social networks does the Social Suite support?", a: "Facebook, Instagram, X/Twitter and LinkedIn today, with the same approval workflows and audit trail as the rest of the platform." },
      { q: "Does the AI write posts for us automatically?", a: "AI assists with captions and hashtags, but every post still goes through your configured approval workflow before it can publish, nothing ships without a human sign-off unless you choose to allow it." },
    ],
  },
  {
    title: "Coverage intelligence",
    items: [
      { q: "How is a coverage score calculated?", a: "Each submission is scored by AI across accuracy, tone, reach and quality. Your team can override any score with a note, and both the original AI score and the override are preserved for audit purposes." },
      { q: "Can journalists see their own scores?", a: "Yes, journalists can see their own coverage history and scores in their portal, which is part of what makes the scoring defensible and not a black box." },
    ],
  },
  {
    title: "For journalists",
    items: [
      { q: "How do journalists sign in?", a: "With a single email and password. Journalists land in one unified portal across all the institutions they cover." },
      { q: "Do journalists need a separate account per institution?", a: "No. One journalist profile works across every institution on CONORBIT, accreditation, passes and submissions are all managed centrally." },
      { q: "Is event selection fair?", a: "Selection criteria are configured by each institution and can be geo-capped or otherwise constrained, and the criteria are visible to applicants rather than hidden." },
    ],
  },
  {
    title: "Security & compliance",
    items: [
      { q: "Is data isolated between institutions?", a: "Yes, enforced through row-level security policies at the database layer, not just application-level checks." },
      { q: "Do you keep an audit trail of admin actions?", a: "Yes. Sensitive actions, role changes, accreditation decisions, score overrides, billing changes, are all logged with who, what and when." },
    ],
  },
];

export default function FaqPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <MarketingNav />

      <section className="pt-32 pb-16 lg:pt-44 lg:pb-20">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <span className="text-xs uppercase tracking-[0.25em] text-accent font-medium">FAQ</span>
          <h1 className="font-display text-4xl md:text-5xl mt-3 text-foreground">Quietly answered.</h1>
          <p className="text-justify text-muted-foreground mt-4 leading-relaxed">
            Questions about the platform, the Social Suite, coverage intelligence, and the
            journalist portal. Looking for billing or plan questions instead? See the{" "}
            <Link to="/pricing" className="text-accent hover:underline">pricing FAQ</Link>.
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="container mx-auto px-4 max-w-3xl space-y-12">
          {SECTIONS.map((section) => (
            <div key={section.title}>
              <h2 className="font-display text-xl text-foreground mb-4">{section.title}</h2>
              <Accordion type="single" collapsible className="space-y-3">
                {section.items.map((it, i) => (
                  <AccordionItem key={i} value={`${section.title}-${i}`} className="bg-card border border-border rounded-xl px-5">
                    <AccordionTrigger className="text-left font-display text-base hover:no-underline">{it.q}</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-sm leading-relaxed">{it.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}
        </div>
      </section>

      <MarketingFinalCta />
      <MarketingFooter />
    </div>
  );
}
